// ============================================================
// Chess Arena — Result Popup
// Victory/Defeat modal with rewards, coin fly, XP bar
// ============================================================

import type { Screen } from '../screenManager';
import type { MatchResult, ChestTier } from '../../core/types';
import gameConfig from '../../core/gameConfig';

const CHEST_TIER_LABELS: Record<ChestTier, string> = {
  bronze: 'Bronze Chest',
  silver: 'Silver Chest',
  gold: 'Gold Chest',
  royal: 'Royal Chest',
};

const CHEST_TIER_ICONS: Record<ChestTier, string> = {
  bronze: '/assets/ui/icon_chest_bronze.png',
  silver: '/assets/ui/icon_chest_silver.png',
  gold: '/assets/ui/icon_chest_gold.png',
  royal: '/assets/ui/icon_chest_royal.png',
};

export class ResultPopup implements Screen {
  readonly name = 'result';
  private container!: HTMLElement;

  private onContinue: (() => void) | null = null;
  private onTryAgain: (() => void) | null = null;

  setOnContinue(cb: () => void): void {
    this.onContinue = cb;
  }

  setOnTryAgain(cb: () => void): void {
    this.onTryAgain = cb;
  }

  init(container: HTMLElement): void {
    this.container = container;
    container.classList.add('result-popup');
    // Content is built dynamically in show()
  }

  show(data?: Record<string, unknown>): void {
    const result = (data?.result as MatchResult) ?? 'victory';
    const level = (data?.level as number) ?? 1;
    const coins = (data?.coins as number) ?? 0;
    const xp = (data?.xp as number) ?? 0;
    const trophyChange = (data?.trophyChange as number) ?? 0;
    const chestTier = data?.chestTier as ChestTier | null ?? null;

    // XP bar state (before and after XP was applied)
    const xpBefore = (data?.xpBefore as number) ?? 0;
    const xpLevelBefore = (data?.xpLevelBefore as number) ?? 1;
    const xpForNextBefore = (data?.xpForNextBefore as number) ?? 100;
    const xpAfter = (data?.xpAfter as number) ?? 0;
    const xpLevelAfter = (data?.xpLevelAfter as number) ?? 1;
    const xpForNextAfter = (data?.xpForNextAfter as number) ?? 100;

    const isVictory = result === 'victory';
    const chargeRewards = data?.chargeRewards as Record<string, number> | undefined;

    // Build charge reward HTML
    let chargeRewardHtml = '';
    if (chargeRewards && Object.keys(chargeRewards).length > 0) {
      const abilityCfg = gameConfig.isInitialized ? gameConfig.getAbilityConfig() : null;
      const items = Object.entries(chargeRewards).map(([abilityId, count]) => {
        const name = abilityCfg?.abilities?.[abilityId]?.displayName ?? abilityId;
        return `<span class="result-charge-item">${name} +${count}</span>`;
      }).join(' ');
      chargeRewardHtml = `
        <div class="result-reward-row">
          <span class="result-reward-label">\u26A1 Charges</span>
          <span class="result-reward-value result-reward-positive">${items}</span>
        </div>
      `;
    }

    // Calculate start and end percentages for the XP bar
    const startPct = xpForNextBefore > 0 ? (xpBefore / xpForNextBefore) * 100 : 0;
    const endPct = xpForNextAfter > 0 ? (xpAfter / xpForNextAfter) * 100 : 0;
    const didLevelUp = xpLevelAfter > xpLevelBefore;

    this.container.innerHTML = `
      <div class="ca-backdrop">
        <div class="ca-modal result-modal">
          ${!isVictory ? '<button class="ca-close-btn" data-ref="closeBtn">\u2715</button>' : ''}

          <div class="result-level-label">Stage ${level}</div>

          <div class="result-title ${isVictory ? 'result-title-victory' : 'result-title-defeat'}">
            ${isVictory ? 'Well Done!' : 'Defeated!'}
          </div>

          ${isVictory ? '<div class="result-star">\u2B50</div>' : ''}

          <div class="result-rewards">
            <div class="result-reward-row">
              <span class="result-reward-label">\uD83D\uDCB0 Coins</span>
              <span class="result-reward-value result-reward-positive">+${coins}</span>
            </div>
            <div class="result-reward-row">
              <span class="result-reward-label">\uD83C\uDFC6 Trophies</span>
              <span class="result-reward-value ${trophyChange >= 0 ? 'result-reward-positive' : 'result-reward-negative'}">
                ${trophyChange >= 0 ? '+' : ''}${trophyChange}
              </span>
            </div>
            <div class="result-reward-row">
              <span class="result-reward-label">\u2B50 XP</span>
              <span class="result-reward-value result-reward-positive">+${xp}</span>
            </div>

            <!-- XP Bar -->
            <div class="result-xp-bar">
              <div style="display: flex; justify-content: space-between; font-size: 11px; color: var(--ca-text-muted); margin-bottom: 4px;">
                <span>Rank ${xpLevelAfter}</span>
                <span>Rank ${xpLevelAfter + 1}</span>
              </div>
              <div class="ca-progress ca-progress-blue">
                <div class="ca-progress-fill" data-ref="xpFill" style="width: ${didLevelUp ? 0 : startPct}%; transition: none;"></div>
              </div>
            </div>

            ${chargeRewardHtml}

            ${isVictory && chestTier ? `
              <div class="result-chest-earned">
                <span class="result-chest-icon"><img src="${CHEST_TIER_ICONS[chestTier]}" alt="" style="width: 32px; height: 32px;"></span>
                <span class="result-chest-text">${CHEST_TIER_LABELS[chestTier]} Earned!</span>
              </div>
            ` : ''}
          </div>

          <div class="result-actions">
            ${isVictory
              ? '<button class="ca-btn ca-btn-green ca-btn-lg" data-ref="continueBtn">Continue</button>'
              : `<button class="ca-btn ca-btn-orange" data-ref="tryAgainBtn">Try Again</button>`
            }
          </div>
        </div>
      </div>
    `;

    // Event listeners
    const continueBtn = this.container.querySelector('[data-ref="continueBtn"]');
    continueBtn?.addEventListener('click', () => this.onContinue?.());

    const tryAgainBtn = this.container.querySelector('[data-ref="tryAgainBtn"]');
    tryAgainBtn?.addEventListener('click', () => this.onTryAgain?.());

    const closeBtn = this.container.querySelector('[data-ref="closeBtn"]');
    closeBtn?.addEventListener('click', () => this.onContinue?.());

    // Animate XP bar fill after a short delay
    const xpFill = this.container.querySelector('[data-ref="xpFill"]') as HTMLElement | null;
    if (xpFill) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          xpFill.style.transition = 'width 0.8s ease-out';
          xpFill.style.width = `${endPct}%`;
        });
      });
    }

    // Trigger coin fly animation if victory
    if (isVictory && coins > 0) {
      this.spawnCoinFlyAnimation(coins);
    }
  }

  hide(): void {
    this.container.innerHTML = '';
  }

  update(): void {
    // No periodic updates
  }

  destroy(): void {
    this.container.innerHTML = '';
  }

  /** Spawn flying coin elements for the reward animation. */
  private spawnCoinFlyAnimation(coinCount: number): void {
    const count = Math.min(coinCount, 8); // Max 8 coin particles
    const modal = this.container.querySelector('.result-modal');
    if (!modal) return;

    const rect = modal.getBoundingClientRect();
    const startX = rect.left + rect.width / 2;
    const startY = rect.top + rect.height * 0.4;

    for (let i = 0; i < count; i++) {
      const coin = document.createElement('div');
      coin.className = 'coin-fly';
      coin.textContent = '\uD83D\uDCB0';

      // Random trajectory
      const angle = (Math.random() - 0.5) * 120;
      const dist = 80 + Math.random() * 60;
      const flyX = Math.cos((angle * Math.PI) / 180) * dist;
      const flyY = -dist;

      coin.style.left = `${startX}px`;
      coin.style.top = `${startY}px`;
      coin.style.setProperty('--fly-x', `${flyX}px`);
      coin.style.setProperty('--fly-y', `${flyY}px`);
      coin.style.setProperty('--fly-end-x', `${flyX * 1.5}px`);
      coin.style.setProperty('--fly-end-y', `${flyY * 2}px`);
      coin.style.animationDelay = `${i * 0.1}s`;

      document.body.appendChild(coin);

      // Clean up after animation
      setTimeout(() => coin.remove(), 1200 + i * 100);
    }
  }
}
